import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { User, Session, AuthError } from '@supabase/supabase-js';
import * as Sentry from '@sentry/react';
import { supabase } from '@shared/lib/supabase/client';
import { usePortalConfig } from '@shared/config';
import { EnrollmentResultSchema, type EnrollmentResult } from '@shared/schemas/rpc';

interface ProfileData {
  fullName: string;
  organization: string;
  functie: string;
  telefoonnummer: string;
  newsletterPreference?: 'casefinder' | 'casefinder_hr' | 'casefinder_hr_daily';
}

interface EnrollmentState {
  needsEnrollment: boolean;
  currentPortals: Array<'lexlabour' | 'lexzone' | 'lexwoo'>;
  targetPortal: 'lexlabour' | 'lexzone' | 'lexwoo';
}

interface UserProfile {
  portal: 'lexlabour' | 'lexzone' | 'lexwoo';
  portal_toegang: Array<'lexlabour' | 'lexzone' | 'lexwoo'>;
  heeft_alle_portals: boolean;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  error: AuthError | null;
  signIn: (email: string, password: string) => Promise<{ needsEnrollment: boolean }>;
  signUp: (email: string, password: string, profile?: ProfileData) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  clearError: () => void;
  enrollmentState: EnrollmentState | null;
  enrollInPortal: (portal: 'lexlabour' | 'lexzone' | 'lexwoo', newsletterPreference: 'none' | 'weekly' | 'daily') => Promise<EnrollmentResult>;
  cancelEnrollment: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<AuthError | null>(null);
  const [enrollmentState, setEnrollmentState] = useState<EnrollmentState | null>(null);
  const { id: portalId } = usePortalConfig();

  useEffect(() => {
    // Use onAuthStateChange as the single source of truth for auth state.
    // The INITIAL_SESSION event fires immediately on registration with the
    // current session from storage, eliminating the need for a separate
    // getSession() call and the race condition it introduces.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (event === 'INITIAL_SESSION') {
        setLoading(false);
      }

      // Clear enrollment state when user signs out
      if (event === 'SIGNED_OUT') {
        setEnrollmentState(null);
      }

      // Update Sentry user context
      if (session?.user) {
        Sentry.setUser({ id: session.user.id });
      } else {
        Sentry.setUser(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      setError(null);

      // 1. Authenticate with Supabase
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;

      // 2. Query user profile for portal validation
      // This prevents users from logging into portals they don't have access to.
      // Without this check, a LexLabour user could log into lexzone.nl and see
      // arbeidsrecht content in the LexZone theme (bug fix).
      const { data: profile, error: profileError } = await supabase
        .from('gebruiker_profielen')
        .select('portal, portal_toegang, heeft_alle_portals')
        .eq('id', data.user.id)
        .single<UserProfile>();

      if (profileError || !profile) {
        // Development: log to console for debugging
        if (import.meta.env.DEV) {
          console.error('Failed to fetch user profile:', profileError);
        }
        // Production: log to Sentry for monitoring
        Sentry.captureException(profileError, {
          level: 'error',
          tags: { context: 'auth', operation: 'profile-fetch' }
        });
        await supabase.auth.signOut();
        throw new Error('Invalid login credentials');
      }

      // 3. Validate portal access
      const currentPortal = portalId; // From usePortalConfig() hook
      const hasPortalAccess =
        profile.portal_toegang?.includes(currentPortal) ||
        profile.heeft_alle_portals;

      if (!hasPortalAccess) {
        // User authenticated successfully but doesn't have access to this portal
        // Set enrollment state instead of signing out - modal will handle enrollment
        setEnrollmentState({
          needsEnrollment: true,
          currentPortals: profile.portal_toegang || [],
          targetPortal: currentPortal,
        });
        return { needsEnrollment: true }; // Return enrollment needed flag
      }

      // 4. Success - auth state will be updated via onAuthStateChange listener
      return { needsEnrollment: false };
    } catch (err) {
      setError(err as AuthError);
      throw err;
    }
  };

  const signUp = async (email: string, password: string, profile?: ProfileData) => {
    try {
      setError(null);
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/login`,
          data: {
            full_name: profile?.fullName,
            organization: profile?.organization,
            functie: profile?.functie,
            telefoonnummer: profile?.telefoonnummer,
            newsletter_preference: profile?.newsletterPreference,
            portal: portalId,  // Include portal ID for correct portal assignment
          }
        }
      });
      if (error) throw error;

      // Supabase returns a fake success with empty identities when the email
      // already exists (to prevent email enumeration). Detect this and throw
      // so RegisterForm can show the "already have an account" message.
      if (data.user && data.user.identities?.length === 0) {
        throw new Error('User already registered');
      }

      // Profile creation is now handled by database trigger (handle_new_user)
      // No need for manual profile insert
    } catch (err) {
      setError(err as AuthError);
      throw err;
    }
  };

  const signOut = async () => {
    try {
      setError(null);
      setEnrollmentState(null); // Clear any pending enrollment

      // Attempt to sign out from Supabase
      const { error } = await supabase.auth.signOut();

      if (error) {
        console.warn('Supabase signOut error, forcing local cleanup:', error);
        // Force local session cleanup even if server signOut fails
        // This handles cases where session is invalid (e.g., after password change)
        setUser(null);
        setSession(null);

        // Clear Sentry user context
        Sentry.setUser(null);

        // Don't throw error - allow signOut to complete locally
        return;
      }

      // Normal signOut succeeded - state will be cleared by onAuthStateChange listener
    } catch (err) {
      console.error('SignOut error:', err);

      // Force local cleanup on any error
      setUser(null);
      setSession(null);
      setEnrollmentState(null);
      Sentry.setUser(null);

      // Don't throw - allow user to proceed even if server signOut failed
    }
  };

  const resetPassword = async (email: string) => {
    try {
      setError(null);
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
    } catch (err) {
      setError(err as AuthError);
      throw err;
    }
  };

  const updatePassword = async (newPassword: string) => {
    try {
      setError(null);
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });
      if (error) throw error;
    } catch (err) {
      setError(err as AuthError);
      throw err;
    }
  };

  const clearError = () => setError(null);

  const enrollInPortal = async (
    portal: 'lexlabour' | 'lexzone' | 'lexwoo',
    newsletterPreference: 'none' | 'weekly' | 'daily'
  ): Promise<EnrollmentResult> => {
    try {
      const { data, error } = await supabase.rpc('enroll_user_in_portal' as never, {
        p_portal: portal,
        p_newsletter_type: newsletterPreference,
      } as never);

      if (error) throw error;

      // Validate response structure with Zod schema
      const result = EnrollmentResultSchema.parse(data);

      // Clear enrollment state and complete login
      setEnrollmentState(null);

      // Refresh user session to update profile
      const { data: { user: updatedUser } } = await supabase.auth.getUser();
      setUser(updatedUser);

      return result;
    } catch (error) {
      console.error('Enrollment failed:', error);
      throw error;
    }
  };

  const cancelEnrollment = async () => {
    setEnrollmentState(null);
    try {
      await supabase.auth.signOut();
    } catch (_err) {
      // Force local cleanup even if signOut fails (consistent with signOut handler above)
      setUser(null);
      setSession(null);
      Sentry.setUser(null);
    }
  };

  const value = {
    user,
    session,
    loading,
    error,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
    clearError,
    enrollmentState,
    enrollInPortal,
    cancelEnrollment,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}